<template>
  <div class="group">
    <font-awesome-icon
      :icon="['fas', 'comment']"
      @click="toggle"
      :class="[{ invisible: !remarkText }]"
      class="text-primary-800 p-2 hover:cursor-pointer group-hover:visible"
    />
    <div
      v-if="isActive"
      class="absolute top-[-9rem] right-[-10rem] mt-2 mr-2 z-20 bg-white p-2 rounded shadow opacity-95"
    >
      <textarea
        v-model="remarkTextModel"
        class="w-64 h-20 p-2 m-4 text-sm rounded border"
        placeholder="Opmerking toevoegen"
      ></textarea>
      <button
        @click.stop="closeRemarkBox"
        class="absolute top-[-12px] right-0 mt-2 mr-1 text-3xl font-semibold"
      >
        &times;
      </button>
      <font-awesome-icon
        :icon="['fas', 'check']"
        class="absolute bottom-2 right-2 hover:cursor-pointer text-1xl"
        :class="{
          'text-green-500': remarkText,
          'text-gray-500 cursor-not-allowed pointer-events-none': !remarkText,
        }"
        @click="remarkText && addRemark(remarkText)"
      />
      <transition name="fade">
        <font-awesome-icon
          v-if="props.showTrashIcon"
          :icon="['fas', 'trash']"
          class="absolute bottom-2 left-2 text-red-500 hover:cursor-pointer text-1xl"
          @click="deleteRemark"
        />
      </transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from "vue";
import {
  useAddRemark,
  useUpdateRemark,
  useDeleteRemark,
} from "@planbord/queries/planbord-api";
import { useBroadcast } from "@planbord/queries/planbord-api";

const props = defineProps<{
  scheduleId: number;
  remarkText: string;
  isActive: boolean;
  hasRemark: boolean;
  showTrashIcon: boolean;
}>();

const emit = defineEmits<{
  (event: "toggle"): void;
  (event: "close"): void;
  (event: "update:remarkText", value: string): void;
  // TODO: deleteRemark event
}>();

function toggle() {
  emit("toggle");
}

const { mutateAsync: broadcast } = useBroadcast();

const { mutateAsync: addRemarkAsync } = useAddRemark();
const { mutateAsync: updateRemarkAsync } = useUpdateRemark();
const { mutateAsync: deleteRemarkAsync } = useDeleteRemark();

const remarkTextModel = computed({
  get: () => props.remarkText,
  set: (newRemarkText) => emit("update:remarkText", newRemarkText),
});

async function addRemark(remarkText: string) {
  let result: { status: unknown; data?: unknown };

  if (props.hasRemark) {
    result = await updateRemarkAsync({
      ScheduleId: props.scheduleId,
      RemarkText: remarkText,
    });
  } else {
    result = await addRemarkAsync({
      ScheduleId: props.scheduleId,
      RemarkText: remarkText,
    });
  }

  closeRemarkBox();

  // Introduce a small delay before updating showTrashIcon and hasRemark
  setTimeout(async () => {
    if (result && result.status === 200) {
      // Broadcast to SignalR
      await broadcast({
        messageType: "remarkAddedOrUpdated",
        payload: {
          Id: props.scheduleId,
          RemarkText: remarkText,
        },
      });
    }
  }, 300);
}

async function deleteRemark() {
  let result;

  if (props.hasRemark) {
    result = await deleteRemarkAsync({
      ScheduleId: props.scheduleId,
    });
    if (result && result.status === 200) {
      // Broadcast to SignalR
      await broadcast({
        messageType: "remarkDeleted",
        payload: {
          Id: props.scheduleId,
        },
      });
    }
  }

  closeRemarkBox();
}

function closeRemarkBox() {
  emit("close");
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease-in-out;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
